import axios from "axios";
import { useContext, useEffect, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import styled from "styled-components";
import theme from "../styles/theme";
import Button from "./Button";
import FlexBox, { FlexGrow } from "./FlexBox";
import Input, { InputTextArea } from "./Input";
import MinimalSelect from "./MinimalSelect";
import { StyledText } from "./Text";
import { Icon as MuiIcon, TableBody, TableHead, TableRow } from "@mui/material";
import { components } from "react-select";
import Icon, { ClickableIcon } from "./Icon";
import Status from "../types/enum/Status";
import { useNavigate } from "react-router-dom";
import { CompetencyType, EmptyCompetency } from "../types/Competency";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { VersionContext } from "../context/VersionContext";
import convertToSelect from "../hooks/useScript";
import SnackBar from "./SnackBar";
import { UserContext } from "../context/UserContext";
import ConfirmChanges from "./ConfirmChanges";
import CreatePrerequisites from "./CreatePrequisites";
import MoveCompetencyModal from "./MoveCompetencyModal";
import OptionGroupModal from "./OptionGroupModal";
import OptionGroupModalNew from "./OptionGroupModalNew";
import { set } from "date-fns";
import { EditDerived } from "../pages/Competencies/EditDerived";
import ConfirmModal from "./ConfirmModal";
import { LegacyConfirmModal } from "../pages/Competencies/LegacyConfirmModal";
import Modal from "./Modal";

const { Option, SingleValue } = components;

export const CustomFlexFixed = styled(FlexBox)`
  width: 100%;
  position: fixed;
  bottom: 0;
  right: 0px;
  background: #ffffff;
  padding: 6px 0px;
  box-shadow: #4f4f4f12 0px -5px 20px;
  // z-index: 999;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 20px 10px;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 170px;
  flex-grow: 0;
`;
const Label = styled.h5`
  text-transform: uppercase;
  color: ${theme.color.darkBlueGray900};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;

const DescriptionLabel = styled(StyledText)`
  color: ${theme.color.blue900};
  font-size: 14px;
  // font-weight: 600;
  display: flex;
  background: rgba(246, 249, 252, 1);
  padding: 10px;
  border-radius: 5px;
  box-shadow: rgba(0, 70, 255, 0.05) 0px 2px 3px 3px inset;
  align-items: center;
  gap: 2px;
`;

const LabelData = styled(StyledText)`
  color: ${theme.color.lightBlueGray500};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.20000000298023224px;
  text-align: left;
  vertical-align: text-top;
`;
const EditStyle = styled(StyledText)`
  border-radius: 5px;
  border: 1px solid #4a556761;
  color: #4a5567;
  padding: 0px 12px;
  margin-left: 20px;
  // color: white;
  display: flex;
  align-items: center;
  // background: #4a5567;
  box-shadow: 0px 1px 1px #ebebeb;
`;

type CourseOverviewModifyType = {
  allCompetencies: any;
  fetchParent: any;
  competencyData: CompetencyType;
  showBackButton?: boolean;
  checkLegacyVersions?: any;
  legacyVersionsData?: any;
  setLegacyVersionsData?: any;
  legacyConfirmOpen?: boolean;
  setLegacyConfirmOpen?: any;
  goBack: () => void;
  onSaveComplete: (data: any) => Promise<void>;
};
const schema = yup
  .object({
    id: yup.string(),
    identity_code: yup.string(),
    title: yup.string().required(),
    description: yup.string(),
    subdomain_id: yup.string(),
    knowledge_area_id: yup.string(),
    pillar_id: yup.string().required(),
    alt_pillar_id: yup.string(),
    required: yup.boolean(),
    // pass_fail: yup.boolean().required(),
    ordinal: yup
      .number()
      .typeError("This field is required and must be numeric")
      .required(),
    min_optional_count: yup
      .number()
      .typeError("This field is required and must be numeric")
      .required(),
    competency_code: yup
      .string()
      .matches(/^(([A-Z]{3})([-.][0-9]{3}))?$/, "Incorrect format!")
      .required("This field is required"),
    credits: yup
      .number()
      .typeError("This field is required and must be numeric")
      .required(),
    instructor_id: yup.string().required(),
    university_code: yup.string().required(),
    curriculum_version: yup.number(),
    prerequisities: yup.array(
      yup.object({
        id: yup.string().required("Select a competency from list"),
        title: yup.string(),
      })
    ),
    core_technical: yup.boolean(),
    math_science: yup.boolean(),
    social_communication_arts: yup.boolean(),
    research: yup.boolean(),
    soft_skill: yup.boolean(),
    xp: yup.boolean(),
  })
  .test(
    "at-least-one-true",
    "At least one of the field must be selected",
    function (value) {
      if (
        !(
          value.core_technical ||
          value.math_science ||
          value.social_communication_arts ||
          value.research ||
          value.soft_skill ||
          value.xp
        )
      ) {
        return this.createError({ path: "core_technical" });
      }
      return true;
    }
  )
  .required();
const SaveButton = styled(Button)`
  background: ${({ theme }) => theme.color.blueButton};
  box-shadow: 0px 2px 5px #808080cc;
  border: none;
  width: 120px;
  min-height: 40px;
`;
export const CancelButton = styled(Button)`
  margin-right: 10px;
  vertical-align: bottom;
  background: ${({ theme }) => theme.color.deleteButton};
  box-shadow: 0px 2px 5px #808080cc;
  border: none;
  width: 120px;
  min-height: 40px;
`;

function CourseOverviewModify({
  allCompetencies,
  competencyData,
  goBack,
  showBackButton = false,
  onSaveComplete,
  fetchParent,
  legacyConfirmOpen,
  setLegacyConfirmOpen,
  legacyVersionsData,
  setLegacyVersionsData,
}: CourseOverviewModifyType) {
  const navigate = useNavigate();
  const {
    register: registerOverview,
    handleSubmit: handleSubmitOverview,
    control: controlOverview,
    watch: watchOverview,
    reset: resetOverview,
    trigger,
    getValues,
    setValue: setValueOverview,
    formState: { isDirty, errors: errorsOverview },
  } = useForm({ mode: "onSubmit", resolver: yupResolver(schema) });
  const { fields, append, remove } = useFieldArray({
    name: "prerequisities",
    control: controlOverview,
    keyName: "key",
  });
  const watchPrerequisitesArray = watchOverview("prerequisities");
  const [competencies, setCompetencies] = useState<
    Array<{
      label: string;
      value: string;
      selected: boolean;
      index: string;
    }>
  >([]);
  const [instructors, setInstructors] = useState<Array<any>>([]);
  const [prerequisites, setPrerequisites] = useState<Array<any>>([]);
  const { version } = useContext(VersionContext);
  const { isModified, setModified, isOpen, setOpen } = useContext(UserContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [isErrorOpen, setIsErrorOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [moveCompetencyOpen, setMoveCompetencyOpen] = useState<boolean>(false);
  const [category, setCategory] = useState<string>("");
  const [count, setCount] = useState({
    name: 0,
    description: 0,
    competency_code: 0,
  });
  const [optionGroupOpen, setOptionGroupOpen] = useState<boolean>(false);
  const [optionGroups, setOptionGroups] = useState<any>([]);
  const [currentOptionGroup, setCurrentOptionGroup] = useState<string>("");
  const [cloneResultsOpen, setCloneResultsOpen] = useState<boolean>(false);
  const [cloneResults, setCloneResults] = useState<any[]>([]);
  const onSubmitOverview = async (data: any) => {
    await onSaveComplete({
      ...data,
      area_map: {
        core_technical: data.core_technical,
        math_science: data.math_science,
        social_communication_arts: data.social_communication_arts,
        research: data.research,
        soft_skill: data.soft_skill,
        xp: data.xp,
      },
    });

    mapData(competencyData);
    setOpen(false);
    setModified(false);
    // resetOverview();
  };
  useEffect(() => {
    // fetchCompetencies();
    fetchInstructors();
    fetchPrereq();
    fetchOptionGroups();
  }, [competencyData]);

  useEffect(() => {
    // resetOverview();
    mapData(competencyData);
  }, [competencyData, instructors]);
  useEffect(() => {
    isDirty && setModified(true);
  }, [isDirty]);
  const mapData = (data: CompetencyType) => {
    setValueOverview("id", data.id);
    setValueOverview("identity_code", data.identity_code);
    setValueOverview("pillar_id", data.pillar_id);
    setValueOverview("subdomain_id", data.subdomain_id);
    setValueOverview("knowledge_area_id", data.knowledge_area_id);
    setValueOverview("title", data.title);
    setValueOverview("description", data.description);
    setValueOverview("credits", data.credits);
    !competencyData?.option_group &&
      setValueOverview("required", data.required);
    // setValueOverview("pass_fail", data.pass_fail);
    setValueOverview("alt_pillar_id", "");
    setValueOverview("competency_code", data.competency_code);
    setValueOverview("id", data.id);
    setValueOverview(
      "instructor_id",
      instructors?.filter((el) => el.label === data?.instructors).length > 0
        ? instructors?.filter((el) => el.label === data?.instructors)[0].value
        : ""
    );
    setValueOverview("min_optional_count", data.min_optional);
    setValueOverview("ordinal", 0);
    setValueOverview("university_code", version.university);
    // setValueOverview("pass_fail", data.pass_fail);
    setValueOverview("core_technical", data.area_map?.core_technical);
    setValueOverview("math_science", data.area_map?.math_science);
    setValueOverview(
      "social_communication_arts",
      data.area_map?.social_communication_arts
    );
    setValueOverview("research", data.area_map?.research);
    setValueOverview("soft_skill", data.area_map?.soft_skill);
    setValueOverview("xp", data.area_map?.xp);
    setValueOverview("curriculum_version", data?.curriculum_version);
  };
  const fetchCompetencies = () => {
    axios
      .get(
        `/competency?subdomain_id=${competencyData.subdomain_id}&university_code=${version.university}`
      )
      .then((res) => {
        mapCompetencies(res.data.competencies);
      })
      .catch(() => { });
  };
  const fetchInstructors = () => {
    axios
      .get(`/instructor`)
      .then((res) => {
        if (res.data.instructors) {
          setInstructors(convertToSelect(res.data.instructors, "name", "id"));
        }
      })
      .catch(() => {
        setInstructors([]);
      });
  };
  const fetchPrereq = () => {
    axios
      .get(`/competency/prerequisite?focus_competency_id=${competencyData.id}`)
      .then((res) => {
        if (res.data.prerequisites) {
          setPrerequisites(res.data.prerequisites);
        } else {
          setPrerequisites([]);
        }
      })
      .catch(() => {
        setPrerequisites([]);
      });
  };
  const mapCompetencies = (data: Array<CompetencyType>) => {
    const custom: Array<{
      label: string;
      value: string;
      selected: boolean;
      index: string;
    }> = [];
    data.forEach((element: any, index: any) => {
      custom.push({
        value: element.id,
        label: element.title,
        selected: false,
        index: "",
      });
    });
    setCompetencies(custom);
  };
  const resetRemovedPillar = (index: any) => {
    const found = filterArrayElement(index);
    if (found) {
      setCompetencies(
        competencies.map((itemfound) =>
          itemfound.value === found.value
            ? { ...itemfound, index: "", selected: false }
            : itemfound
        )
      );
    }
  };
  const updateSelectedPillars = (val: any, index: any) => {
    const found = filterArrayElement(index);
    if (found) {
      setCompetencies(
        competencies.map((itemfound) =>
          itemfound.value === found.value
            ? { ...itemfound, index: "", selected: false }
            : itemfound.value === val.value
              ? { ...itemfound, index: String(index), selected: true }
              : itemfound
        )
      );
    } else {
      setCompetencies(
        competencies.map((item) =>
          item.value === val.value
            ? { ...item, index: String(index), selected: true }
            : item
        )
      );
    }
  };
  function filterArrayElement(index: string) {
    return competencies.filter((element) => {
      return String(element.index) === String(index);
    })[0];
  }
  const addPrereq = () => {
    setLoading(true);
    if (
      !watchPrerequisitesArray ||
      !watchPrerequisitesArray[0] ||
      watchPrerequisitesArray[0].id === ""
    ) {
      trigger("prerequisities");
    } else {
      axios
        .post("/competency/prerequisite/new", {
          id: competencyData.id,
          focus_competency_id: competencyData.id,
          prerequisite_competency_id: watchPrerequisitesArray[0].id,
          weight: 0.0,
          university_code: version.university,
        })
        .then((res) => {
          setMessage("Created prerequisites successfully");
          setIsSnackbarOpen(true);
          setLoading(false);
          fetchPrereq();
        })
        .catch(function (error) {
          if (error.response) {
            setMessage(
              error.response.data.error || "Failed to create prerequisites!"
            );
            setIsErrorOpen(true);
          } else if (error.request) {
          } else {
            setMessage(error.response.data.error);
            setIsErrorOpen(true);
          }
          setLoading(false);
        });
    }
  };

  const handleModal = (category: string) => {
    setMoveCompetencyOpen(true);
    setCategory(category);
  };
  const discardFunction = () => {
    setOpen(false);
    setModified(false);
    // resetOverview();
    mapData(competencyData);
  };
  const handleInputChange = () => {
    localStorage.setItem("modified", "true");
  };

  const fetchOptionGroups = () => {
    axios
      .get(`/optiongroups?curriculum_version=${version.version_number}`)
      .then((res) => {
        if (res.data) {
          setOptionGroups(res.data.optionGroups);
        }
      })
      .catch(() => { });
  };

  const storedSelectedProgram = localStorage.getItem("selectedVersion");
  const parsedVersion = storedSelectedProgram
    ? JSON.parse(storedSelectedProgram)
    : null;

  const [selectedVersion, setSelectedVersion] = useState<any>(parsedVersion);

  const [editDerivedOpen, setEditDerivedOpen] = useState<boolean>(false);
  const [confirmDeriveDelete, setConfirmDeriveDelete] = useState<boolean>(false);

  const handleDeriveSave = (
    selectedCompetency: string,
    currentCompetencyId: string,
    sourceVersion: number
  ) => {
    axios
      .post("/competency/reset-derived-from", {
        competency_template_id: currentCompetencyId,
        new_source_competency_template_id: selectedCompetency,
        new_source_version_id: sourceVersion,
      })
      .then(() => {
        setEditDerivedOpen(false);
        fetchParent();
        setIsSnackbarOpen(true);
        setMessage("Derived competency updated successfully");
        setConfirmDeriveDelete(false);
      })
      .catch((err) => {
        setIsErrorOpen(true);
        console.log(err?.response);
        setMessage(
          err?.response?.data?.error || "Failed to edit derived competency"
        );
      });
  };

  const saveClone = (
    competency_code: string,
    source_version: number,
    target_versions: number[]
  ) => {
    axios.put("/competency/auto-propagate", {
      competency_code,
      source_version,
      target_versions
    }).then((response) => {
      setLegacyConfirmOpen(false);
      
      const results = response.data.results || [];
      setCloneResults(results);
      setCloneResultsOpen(true);
    }).catch((err) => {
      setIsErrorOpen(true);
      console.log(err?.response);
      setMessage(
        err?.response?.data?.error || "Failed to clone competency"
      );
    });
  };

  return (
    <>
      {legacyConfirmOpen && (
        <LegacyConfirmModal
          saveClone={saveClone}
          competencyData={{
            competency_code: competencyData.competency_code,
            curriculum_version: competencyData.curriculum_version ?? 0,
          }}
          legacyVersionsData={legacyVersionsData}
          isOpen={legacyConfirmOpen}
          setOpen={setLegacyConfirmOpen}
        />
      )}
      {cloneResultsOpen && (
        <Modal
          isOpen={cloneResultsOpen}
          setOpen={setCloneResultsOpen}
          // title="Clone Results"
        >
          <div style={{ maxHeight: "500px", overflowY: "auto"}}>
            <div style={{fontWeight:'bold', marginBottom:5}}>Clone Results</div>
            {cloneResults.map((result: any, index: number) => (
              <div
                key={index}
                style={{
                  padding: "15px",
                  marginBottom: "10px",
                  borderRadius: "5px",
                  backgroundColor: result.success ? "#d4edda" : "#f8d7da",
                  borderLeft: `4px solid ${result.success ? "#28a745" : "#dc3545"}`,
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: result.success ? "#155724" : "#721c24",
                    marginBottom: "5px",
                  }}
                >
                  Version {legacyVersionsData?.find((v: any) => v.version_number === result.target_version)?.display_version || result.target_version}: {result.success ? "✓ Success" : "✗ Failed"}
                </div>
                {result.error && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: result.success ? "#155724" : "#721c24",
                    }}
                  >
                    {result.error}
                  </div>
                )}
              </div>
            ))}
            <div style={{ marginTop: "20px", textAlign: "right", minWidth: "120px" }}>
              <Button
                onClick={() => setCloneResultsOpen(false)}
                style={{
                  background: theme.color.blueButton,
                  color: "white",
                  border: "none",
                  width: "120px",
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {optionGroupOpen && (
        <OptionGroupModalNew
          fetchOptionGroups={fetchOptionGroups}
          optionGroups={optionGroups}
          setOptionGroups={setOptionGroups}
          title="Add to Competency Option Groups"
          isOpen={optionGroupOpen}
          setOpen={setOptionGroupOpen}
          competencyData={competencyData}
          fetchParent={(optionGroup: string) => {
            setCurrentOptionGroup(optionGroup);
            fetchParent();
          }}
          setIsSnackbarOpen={setIsSnackbarOpen}
          setMessage={setMessage}
          setIsErrorOpen={setIsErrorOpen}
        />
      )}
      {editDerivedOpen && (
        <EditDerived
          handleSave={handleDeriveSave}
          currentCompetencyId={competencyData?.id}
          derivedProgram={competencyData?.derived_from_program}
          isOpen={editDerivedOpen}
          setOpen={setEditDerivedOpen}
        />
      )}
      <SnackBar
        isOpen={isSnackbarOpen}
        setOpen={setIsSnackbarOpen}
        message={message}
        type="success"
      />
      <SnackBar
        isOpen={isErrorOpen}
        setOpen={setIsErrorOpen}
        header="Error!"
        message={message}
        type="error"
      />
      {isOpen && (
        <ConfirmChanges
          discardFunction={discardFunction}
          isOpen={isOpen}
          setOpen={setOpen}
          confirmFunction={handleSubmitOverview(onSubmitOverview)}
          title={"Do you want to save your changes?"}
          description={
            "If you leave page, any changes you have made will be lost."
            // "Your changes will be lost if you don't save them."
          }
        />
      )}

      {
        <ConfirmModal
          isOpen={confirmDeriveDelete}
          setOpen={setConfirmDeriveDelete}
          confirmFunction={() => handleDeriveSave("", competencyData.id, 0)}
          title={"Unlink Derived Competency"}
          description={
            "Are you sure you want to remove this derived relationship?"
          }
        />
      }
      {modalOpen && (
        <CreatePrerequisites
          fetchParent={() => { }}
          fetchPrereq={fetchPrereq}
          prerequisites={prerequisites}
          competencyData={competencyData}
          competencies={allCompetencies}
          discardFunction={discardFunction}
          isOpen={modalOpen}
          setOpen={setModalOpen}
          confirmFunction={handleSubmitOverview(onSubmitOverview)}
          title={"Prerequisites"}
          description={
            "If you leave page, any changes you have made will be lost."
            // "Your changes will be lost if you don't save them."
          }
        />
      )}
      {moveCompetencyOpen && (
        <MoveCompetencyModal
          message={message}
          setMessage={setMessage}
          setIsErrorOpen={setIsErrorOpen}
          setIsSnackbarOpen={setIsSnackbarOpen}
          competencyData={competencyData}
          category={category}
          confirmFunction={() => {
            fetchParent();
          }}
          discardFunction={() => setMoveCompetencyOpen(false)}
          isOpen={moveCompetencyOpen}
          setOpen={setMoveCompetencyOpen}
          title="Move Competency"
        />
      )}
      <form
        // className="visible-scrollbar"
        onSubmit={(e: any) => handleSubmitOverview(onSubmitOverview)(e)}

        style={{
          maxHeight: "calc(100vh - 224px)",
          minHeight: "calc(100vh - 224px)",
          overflowY: "scroll",
        }}
      >
        {competencyData.derived_from_id && (
          <CustomFlexPlain>
            <DescriptionLabel
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "underline",
                }}
              >
                <MuiIcon
                  sx={{
                    // mr: 1,
                    // paddingLeft: "14px",
                    fontSize: "24px",
                    mr: 1,
                  }}
                >
                  link
                </MuiIcon>
                Derived from{" "}
                <span
                  style={{
                    fontStyle: "italic",
                    margin: "0px 5px",
                    fontWeight: "600",
                  }}
                >
                  {competencyData?.derived_from_code}
                </span>{" "}
                in{" "}
                <span
                  style={{
                    fontStyle: "italic",
                    margin: "0px 5px",
                    fontWeight: "600",
                  }}
                >
                  {competencyData?.derived_from_program +
                    " - " +
                    competencyData?.derived_from_version}
                </span>
                {/* <MuiIcon
                    sx={{
                      // mr: 1,
                      // paddingLeft: "14px",
                      fontSize: "24px",
                      color:'red'
                    }}
                  >
                    clear
                  </MuiIcon> */}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 1,
                  color: "black",
                  fontWeight: "normal",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  opacity: competencyData.has_cards ? 0.5 : 1,
                  pointerEvents: competencyData.has_cards ? "none" : "auto",
                }}
              >
                {/* <EditStyle
                  onClick={() =>
                    !competencyData.has_cards && handleEditDerived()
                  }
                  className="reduce_opct_onhover"
                  style={{
                    marginLeft: 5,
                    minWidth: 70,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <MuiIcon
                    sx={{
                      // mr: 1,
                      // paddingLeft: "14px",
                      fontSize: "16px",
                    }}
                  >
                    edit
                  </MuiIcon>
                  edit
                </EditStyle> */}
                <EditStyle
                  onClick={() => {
                    !competencyData.has_cards && setConfirmDeriveDelete(true);
                  }}
                  className="reduce_opct_onhover"
                  style={{
                    marginLeft: 5,
                    minWidth: 70,
                    color: "#1D4ED8",
                    border: `1px solid #1D4ED8`,
                  }}
                >
                  <MuiIcon
                    sx={{
                      // mr: 1,
                      // paddingLeft: "14px",
                      fontSize: "24px",
                      color: "#1D4ED8",
                      marginRight: 1,
                    }}
                  >
                    link_off
                  </MuiIcon>
                  Unlink
                </EditStyle>
              </div>
            </DescriptionLabel>
            {competencyData.has_cards && <div title="Associated with student cards" style={{ display: "flex", alignItems: "center", marginLeft: 20, color: '#344cd5', cursor: 'pointer' }}>
              **
              {/* <MuiIcon
                sx={{
                  // mr: 1,
                  // paddingLeft: "14px",
                  fontSize: "24px",
                  color: "#344cd5",
                  marginRight: 0.5,
                }}
              >
                assignment_ind
              </MuiIcon>
              <span style={{ color: theme.color.blue700, fontSize: 14 }}>
                Associated with student cards
              </span> */}
            </div>}
          </CustomFlexPlain>
        )}
        {!competencyData.derived_from_id && (
          <CustomFlexPlain>
            {" "}
            {/* <CustomGrowPlain><Label></Label></CustomGrowPlain> */}
            <DescriptionLabel>
              Independent competency
              <div
                style={{
                  display: "flex",
                  gap: 1,
                  color: "black",
                  fontWeight: "normal",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  opacity: competencyData.has_cards ? 0.5 : 1,
                  pointerEvents: competencyData.has_cards ? "none" : "auto",
                }}
              >
                {/* <EditStyle
                  onClick={() =>
                    !competencyData.has_cards && handleEditDerived()
                  }
                  className="reduce_opct_onhover"
                  style={{
                    marginLeft: 5,
                    minWidth: 70,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <MuiIcon
                    sx={{
                      // mr: 1,
                      // paddingLeft: "14px",
                      fontSize: "16px",
                    }}
                  >
                    edit
                  </MuiIcon>
                  edit
                </EditStyle> */}
              </div>
            </DescriptionLabel>
          </CustomFlexPlain>
        )}
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pillar</Label>
          </CustomGrowPlain>
          <FlexGrow style={{ display: "flex", alignItems: "center" }}>
            <MuiIcon
              sx={{
                mr: 1,
                padding: "4px",
                background: competencyData.graphics.iconbg,
                boxShadow: competencyData.graphics.iconshadow,
                borderRadius: "100px",
                fontSize: "16px",
                verticalAlign: "sub",
              }}
            >
              {competencyData.graphics.icon}
            </MuiIcon>
            <LabelData style={{ display: "flex", alignItems: "center" }}>
              {competencyData.pillar_title}
              {!competencyData?.derived_from_id && (
                <EditStyle
                  onClick={() => handleModal("pillar")}
                  className="reduce_opct_onhover"
                >
                  <MuiIcon
                    sx={{
                      // mr: 1,
                      // paddingLeft: "14px",
                      fontSize: "16px",
                    }}
                  >
                    edit
                  </MuiIcon>
                  edit
                </EditStyle>
              )}
            </LabelData>
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Subdomain</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <LabelData style={{ display: "flex", alignItems: "center" }}>
              {competencyData.subdomain_title}
              {!competencyData?.derived_from_id && (
                <EditStyle
                  onClick={() => handleModal("subdomain")}
                  className="reduce_opct_onhover"
                >
                  <MuiIcon
                    sx={{
                      // mr: 1,
                      fontSize: "16px",
                    }}
                  >
                    edit
                  </MuiIcon>
                  edit
                </EditStyle>
              )}
            </LabelData>
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Knowledge Area</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <LabelData style={{ display: "flex", alignItems: "center" }}>
              {competencyData.knowledge_area_title}{" "}
              {!competencyData?.derived_from_id && (
                <EditStyle
                  onClick={() => handleModal("area")}
                  className="reduce_opct_onhover"
                >
                  <MuiIcon
                    sx={{
                      // mr: 1,
                      fontSize: "16px",
                    }}
                  >
                    edit
                  </MuiIcon>
                  edit
                </EditStyle>
              )}
            </LabelData>
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Competency Code</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="competency_code"
                  control={controlOverview}
                  render={({ field }) => (
                    <>
                      <Input
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        isFullWidth
                        maxLength={32}
                        value={field.value}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCount({
                            ...count,
                            competency_code: e.target.value.length,
                          });
                        }}
                      />
                      <FlexGrow style={{ width: "60%", textAlign: "end" }}>
                        <StyledText
                          style={{ fontSize: "14px" }}
                          color={theme.color.lightBlueGray500}
                        >
                          {count.competency_code || field?.value?.length}/32
                        </StyledText>
                      </FlexGrow>
                    </>
                  )}
                />
                {/* <Input
                  isFullWidth
                  {...registerOverview("title", { required: true })}
                /> */}
              </FlexGrow>
            </FlexBox>
            {errorsOverview.competency_code && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                {errorsOverview.competency_code.message}
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Identity code</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <Input
              disabled={competencyData?.derived_from_id ? true : false}
              type="text"
              max={999999}
              min={0}
              isFullWidth
              {...registerOverview("identity_code", { required: true })}
            />
            {errorsOverview.credits && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                {errorsOverview.credits.message}
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Title</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="title"
                  control={controlOverview}
                  render={({ field }) => (
                    <>
                      <Input
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        isFullWidth
                        maxLength={256}
                        value={field.value}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCount({ ...count, name: e.target.value.length });
                        }}
                      />
                      <FlexGrow style={{ width: "60%", textAlign: "end" }}>
                        <StyledText
                          style={{ fontSize: "14px" }}
                          color={theme.color.lightBlueGray500}
                        >
                          {count.name || field?.value?.length}/256
                        </StyledText>
                      </FlexGrow>
                    </>
                  )}
                />
                {/* <Input
                  isFullWidth
                  {...registerOverview("title", { required: true })}
                /> */}
              </FlexGrow>
            </FlexBox>
            {errorsOverview.title && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Description</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "60%" }}>
                <Controller
                  name="description"
                  control={controlOverview}
                  render={({ field }) => (
                    <>
                      <InputTextArea
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        isFullWidth
                        style={{ minHeight: "100px", width: "100%" }}
                        value={field.value}
                        maxLength={2048}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCount({
                            ...count,
                            description: e.target.value.length,
                          });
                        }}
                      />
                      <FlexGrow style={{ textAlign: "end" }}>
                        <StyledText
                          style={{ fontSize: "14px" }}
                          color={theme.color.lightBlueGray500}
                        >
                          {count.description || field?.value?.length}/2048
                        </StyledText>
                      </FlexGrow>
                    </>
                  )}
                />
              </FlexGrow>
            </FlexBox>
            {errorsOverview.description && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Credits</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <Input
              disabled={competencyData?.derived_from_id ? true : false}
              type="number"
              max={999999}
              min={0}
              isFullWidth
              {...registerOverview("credits", { required: true })}
            />
            {errorsOverview.credits && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                {errorsOverview.credits.message}
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Required</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <Controller
              control={controlOverview}
              name="required"
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <input
                    disabled={
                      competencyData.option_group ||
                        competencyData?.derived_from_id
                        ? true
                        : false
                    }
                    type="radio"
                    id="yes"
                    onBlur={onBlur}
                    checked={value === true}
                    onChange={() => onChange(true)}
                    style={{ marginRight: "4px" }}
                  />
                  <LabelData
                    style={{ marginRight: "16px" }}
                    color={theme.color.lightBlueGray500}
                  >
                    Yes
                  </LabelData>
                  <input
                    disabled={
                      competencyData.option_group ||
                        competencyData?.derived_from_id
                        ? true
                        : false
                    }
                    type="radio"
                    id="no"
                    checked={value === false}
                    onChange={() => onChange(false)}
                    onBlur={onBlur}
                    style={{ marginRight: "4px" }}
                  />
                  <LabelData color={theme.color.lightBlueGray500}>No</LabelData>
                </>
              )}
            />
            {errorsOverview.required && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        {/* <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pass/Fail</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <Controller
              control={controlOverview}
              name="pass_fail"
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <>
                  <input
                    type="radio"
                    id="true"
                    onBlur={onBlur}
                    checked={value === true}
                    onChange={() => onChange(true)}
                    style={{ marginRight: "4px" }}
                  />
                  <LabelData
                    style={{ marginRight: "16px" }}
                    color={theme.color.lightBlueGray500}
                  >
                    Yes
                  </LabelData>
                  <input
                    type="radio"
                    id="false"
                    checked={value === false}
                    onChange={() => onChange(false)}
                    onBlur={onBlur}
                    style={{ marginRight: "4px" }}
                  />
                  <LabelData color={theme.color.lightBlueGray500}>No</LabelData>
                </>
              )}
            />
            {errorsOverview.required && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain> */}
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Prerequisites</Label>
          </CustomGrowPlain>
          <FlexGrow>
            {prerequisites?.map((field, index) => (
              <>
                <span
                  key={field.key}
                  style={{
                    marginBottom: "8px",
                    color: "black",
                    background: theme.color.lightBlueButton,
                    borderRadius: 5,
                    marginRight: 4,
                    padding: 3,
                  }}
                >
                  {field?.prerequisite_competency_code}
                  {/* {index !== prerequisites.length-1 && ", "} */}
                  {/* <Controller
                    rules={{
                      required: "Please select the competency",
                    }}
                    control={controlOverview}
                    name={`prerequisities.${index}.id` as const}
                    defaultValue={field.id as any}
                    render={({
                      field: { onChange, onBlur, name, ref, value },
                    }) => (
                      <MinimalSelect
                        name={name}
                        menuPortalTarget={document.body}
                        value={competencies.find(
                          (comp) => comp.value === value
                        )}
                        onChange={(val: any) => {
                          updateSelectedPillars(val, index);
                          onChange(val.value);
                        }}
                        styles={{
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                            color: theme.color.lightBlueGray800,
                          }),
                        }}
                        onBlur={onBlur}
                        isSearchable={false}
                        options={competencies.filter((elem) => !elem.selected)}
                      />
                    )}
                  /> */}
                </span>
                {errorsOverview.prerequisities && (
                  <p
                    style={{
                      marginTop: 0,
                      color: theme.color.red800,
                    }}
                  >
                    {errorsOverview.prerequisities[index]?.id?.message}
                  </p>
                )}
              </>
            ))}
            {fields.length === 0 ? (
              <FlexBox
                style={{ marginTop: prerequisites.length > 0 ? "10px" : "0px" }}
              >
                <FlexGrow>
                  <Button
                    // disabled={
                    //   competencyData?.derived_from_id ||
                    //   version.status === "Active Prime"
                    //     ? true
                    //     : false
                    // }
                    className="reduce_opct_onhover"
                    // disabled={fields.length >= 5}
                    onClick={(e) => {
                      // if (version.status !== "Active Prime") {
                      e.preventDefault();
                      e.stopPropagation();
                      setModalOpen(true);
                      // } else {
                      //   e.preventDefault();
                      //   e.stopPropagation();
                      // }
                    }}
                    // onClick={(e) => {
                    //   e.preventDefault();
                    //   e.stopPropagation();
                    //   append({
                    //     id: "",
                    //     title: "",
                    //   });
                    // }}
                    style={{
                      color: "white",
                      border: "none",
                      // minHeight: "32px",
                      fontSize: "14px",
                      background: theme.color.blueButton,
                    }}
                    status={Status.primary}
                  >
                    <FlexBox alignItems="center">
                      <Icon name={"edit"}></Icon>
                      <span>Edit</span>
                    </FlexBox>
                  </Button>
                </FlexGrow>
              </FlexBox>
            ) : (
              <FlexBox style={{ marginTop: "5px" }}>
                <FlexGrow>
                  <Button
                    onClick={(e) => {
                      resetRemovedPillar(0);
                      e.preventDefault();
                      remove(0);
                    }}
                    style={{
                      minHeight: "32px",
                      fontSize: "14px",
                      background: "rgb(166, 54, 55)",
                      color: "white",
                      marginRight: "5px",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={competencyData?.derived_from_id ? true : false}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addPrereq();
                    }}
                    style={{ minHeight: "32px", fontSize: "14px" }}
                  >
                    Save
                  </Button>
                </FlexGrow>
              </FlexBox>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Option Group</Label>
          </CustomGrowPlain>
          <div>
            <div style={{ color: "black" }}>
              {competencyData?.option_group?.title}
            </div>
            <div>
              <Button
                disabled={competencyData?.derived_from_id ? true : false}
                className="reduce_opct_onhover"
                // disabled={fields.length >= 5}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOptionGroupOpen(true);
                }}
                // onClick={(e) => {
                //   e.preventDefault();
                //   e.stopPropagation();
                //   append({
                //     id: "",
                //     title: "",
                //   });
                // }}
                style={{
                  color: "white",
                  border: "none",
                  // minHeight: "32px",
                  fontSize: "14px",
                  background: theme.color.blueButton,
                }}
                status={Status.primary}
              >
                <Icon size={3} name={"add"}></Icon>Add to Competency Option
                Group
              </Button>
            </div>
          </div>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Area Map</Label>
          </CustomGrowPlain>
          <FlexGrow style={{ width: "100%" }}>
            <div
              style={{
                background: theme.color.blue50,
                width: "60%",
                borderRadius: 5,
                padding: "15px 0px",
              }}
            >
              <Controller
                control={controlOverview}
                name="core_technical"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <>
                    <div style={{ minWidth: "60%", paddingLeft: 15 }}>
                      <input
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        type="checkbox"
                        checked={value}
                        onChange={(e: any) => {
                          onChange(!value);
                        }}
                      />
                      <LabelData
                        style={{ marginLeft: "16px" }}
                        color={theme.color.lightBlueGray500}
                      >
                        Core Technical
                      </LabelData>
                    </div>
                  </>
                )}
              />

              <Controller
                control={controlOverview}
                name="math_science"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <>
                    <div style={{ minWidth: "40%", paddingLeft: 15 }}>
                      <input
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        type="checkbox"
                        checked={value}
                        onChange={(e: any) => {
                          onChange(!value);
                        }}
                      />
                      <LabelData
                        style={{ marginLeft: "16px" }}
                        color={theme.color.lightBlueGray500}
                      >
                        Math Science
                      </LabelData>
                    </div>
                  </>
                )}
              />

              <Controller
                control={controlOverview}
                name="social_communication_arts"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <>
                    <div style={{ minWidth: "60%", paddingLeft: 15 }}>
                      <input
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        type="checkbox"
                        checked={value}
                        onChange={(e: any) => {
                          onChange(!value);
                        }}
                      />
                      <LabelData
                        style={{ marginLeft: "16px" }}
                        color={theme.color.lightBlueGray500}
                      >
                        Social Communication Arts
                      </LabelData>
                    </div>
                  </>
                )}
              />

              <Controller
                control={controlOverview}
                name="research"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <>
                    <div style={{ minWidth: "40%", paddingLeft: 15 }}>
                      <input
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        type="checkbox"
                        checked={value}
                        onChange={(e: any) => {
                          onChange(!value);
                        }}
                      />
                      <LabelData
                        style={{ marginLeft: "16px" }}
                        color={theme.color.lightBlueGray500}
                      >
                        Research
                      </LabelData>
                    </div>
                  </>
                )}
              />
              <Controller
                control={controlOverview}
                name="soft_skill"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <>
                    <div style={{ minWidth: "40%", paddingLeft: 15 }}>
                      <input
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        type="checkbox"
                        checked={value}
                        onChange={(e: any) => {
                          onChange(!value);
                        }}
                      />
                      <LabelData
                        style={{ marginLeft: "16px" }}
                        color={theme.color.lightBlueGray500}
                      >
                        Soft Skill
                      </LabelData>
                    </div>
                  </>
                )}
              />
              <Controller
                control={controlOverview}
                name="xp"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <>
                    <div style={{ minWidth: "40%", paddingLeft: 15 }}>
                      <input
                        disabled={
                          competencyData?.derived_from_id ? true : false
                        }
                        type="checkbox"
                        checked={value}
                        onChange={(e: any) => {
                          onChange(!value);
                        }}
                      />
                      <LabelData
                        style={{ marginLeft: "16px" }}
                        color={theme.color.lightBlueGray500}
                      >
                        XP
                      </LabelData>
                    </div>
                  </>
                )}
              />
            </div>
            {errorsOverview.core_technical && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                {errorsOverview.core_technical.message}
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain style={{ marginBottom: "100px" }}>
          <CustomGrowPlain>
            <Label>Responsible instructor</Label>
          </CustomGrowPlain>
          {instructors && (
            <FlexGrow>
              <Controller
                name="instructor_id"
                control={controlOverview}
                render={({ field }) => (
                  <MinimalSelect
                    isDisabled={competencyData?.derived_from_id ? true : false}
                    {...field}
                    value={instructors.filter((el) => el.id === field.value)}
                    onChange={(data: any) => {
                      field.onChange(data.value);
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                        color: theme.color.lightBlueGray800,
                      }),
                      control: (base) => ({
                        ...base,
                        width: "75%",
                      }),
                    }}
                    options={instructors}
                  />
                )}
              />
              {errorsOverview.instructor_id && (
                <p
                  style={{
                    marginTop: 0,
                    color: theme.color.red800,
                  }}
                >
                  {errorsOverview.instructor_id.message}
                </p>
              )}
            </FlexGrow>
          )}
        </CustomFlexPlain>
        <CustomFlexFixed>
          {showBackButton && (
            <Button
              key={0}
              style={{ marginLeft: "30px" }}
              type="button"
              onClick={() => {
                goBack();
              }}
              status={Status.primary}
              isOutline
            >
              <Icon style={{ paddingRight: "8px" }} name={"arrow_back"} /> Back
            </Button>
          )}
          <FlexGrow
            style={{
              textAlign: "end",
              marginRight: "30px",
              padding: "10px 0px",
            }}
          >
            <CancelButton
              className="reduce_opct_onhover"
              key={1}
              type="button"
              onClick={() => {
                navigate("/modify/competency");
              }}
              status={Status.danger}
              isOutline={false}
            >
              Cancel
            </CancelButton>
            <SaveButton
              disabled={competencyData?.derived_from_id ? true : false}
              className="reduce_opct_onhover"
              key={2}
              type="submit"
              status={Status.primary}
              isOutline={false}
            >
              <Icon style={{ paddingRight: "8px" }} name={"save"} /> Save
            </SaveButton>
          </FlexGrow>
        </CustomFlexFixed>
      </form>
    </>
  );
}
export default CourseOverviewModify;
